import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { CircularProgress } from '@euler/components/CircularProgress';
import { Colors, StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LocalMediaSourceStatus } from '@euler/model';
import { useSharedValueFrom } from '@euler/utils/hooks';
import { memo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

export const MediaUploadStatusOverlay = memo(
  ({
    status,
    progress,
    progressViewSize,
    progressStrokeColor = '#aaa',
    style,
    showSuccessCheckmark,
    onRetry,
    ...props
  }: {
    status: LocalMediaSourceStatus;
    error?: Error;
    progress?: number;
    style?: StyleProp<ViewStyle>;
    progressViewSize: number;
    progressStrokeWidth?: number;
    progressStrokeColor?: string;
    showSuccessCheckmark?: boolean;
    iconContainerSize?: number;
    errorIconSize?: number;
    waitIconSize?: number;
    onRetry?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const progressStrokeWidth = props.progressStrokeWidth ?? rpx(4);
    const errorIconSize = props.errorIconSize ?? rpx(24);
    const waitIconSize = props.waitIconSize ?? rpx(20);
    const iconContainerSize = props.iconContainerSize ?? rpx(40);

    const [progressValue] = useSharedValueFrom(
      status === 'uploading' || status === 'compressing' ? progress ?? 0 : 0,
    );

    if (status === 'uploaded' && !showSuccessCheckmark) {
      return null;
    }

    return (
      <Center
        css={`
          background-color: ${status !== 'uploaded'
            ? 'rgba(0, 0, 0, 0.5)'
            : 'transparent'};
        `}
        style={style}
        pointerEvents={'box-none'}
      >
        {status === 'uploaded' ? (
          <Center
            pointerEvents="none"
            css={`
              position: absolute;
              right: ${rpx(5)}px;
              bottom: ${rpx(5)}px;
              width: ${rpx(40)}px;
              height: ${rpx(40)}px;
              background-color: ${StatusColors.Success};
              border-radius: ${rpx(8)}px;
            `}
          >
            <Icon name="check" size={rpx(12)} color="#fff" />
          </Center>
        ) : null}
        {status === 'uploading' || status === 'compressing' ? (
          <CircularProgress
            progress={progressValue}
            size={progressViewSize}
            stroke={progressStrokeColor}
            strokeWidth={progressStrokeWidth}
          >
            {status === 'compressing' ? (
              <Center style={StyleSheet.absoluteFill}>
                <Typography.Label light size={rpx(8)} color={Colors.White5}>
                  压缩中
                </Typography.Label>
              </Center>
            ) : null}
          </CircularProgress>
        ) : null}
        {status === 'error' ? (
          <TouchableOpacity
            onPress={onRetry}
            css={`
              position: absolute;
              right: 0px;
              bottom: 0px;
              align-items: center;
              justify-content: center;
              width: ${iconContainerSize}px;
              height: ${iconContainerSize}px;
            `}
          >
            <Icon
              name="error"
              size={errorIconSize}
              color={StatusColors.Danger}
            />
          </TouchableOpacity>
        ) : null}
        {status === 'queued' ? (
          <Center
            css={`
              position: absolute;
              right: 0px;
              bottom: 0px;
              align-items: center;
              justify-content: center;
              width: ${iconContainerSize}px;
              height: ${iconContainerSize}px;
            `}
          >
            <Icon name="clock" size={waitIconSize} color="#eee" />
          </Center>
        ) : null}
      </Center>
    );
  },
);
